/**
 * Where the step popover sits (register fix round 3) — a pure function of
 * measured rectangles, so the rules can be tested without a browser.
 *
 * Round 2 clamped against a guessed width (320 px) and a phone's real
 * confirm question measured 489 px, so the popover still ran off the
 * screen. Here nothing is guessed: `ActionPopover` renders once hidden,
 * measures itself in a layout effect, and asks this module where to go
 * before the first paint.
 */

export interface Rect {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface Size {
  width: number;
  height: number;
}

/** The part of the viewport the anchor can actually be seen in (below a sticky head, above a sticky foot). */
export interface Band {
  top: number;
  bottom: number;
}

export interface Placement {
  left: number;
  top: number;
  /** `above` once there is no room under the trigger — the caret then points down. */
  side: "below" | "above";
  /** The caret's centre, in px from the popover's left edge: under the trigger's centre, not at a fixed corner. */
  caret: number;
}

/** Never closer to a viewport edge than this. */
export const POPOVER_EDGE = 8;
/** Trigger to popover: room for the 9 px caret plus one pixel of air. */
export const POPOVER_GAP = 10;
/** The caret stays this far inside the popover's own corners. */
const CARET_INSET = 14;

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * Left or right edge: a trigger in the right half of the screen (a table's
 * step column) lines the popover up with its own right edge and grows it to
 * the left; one in the left half (a phone card, where the trigger sits at the
 * card's start) lines it up with its left edge and grows it to the right.
 * Either way it is then clamped to `POPOVER_EDGE` from both viewport edges.
 *
 * Below or above: below while it fits inside `band`, above once it does not
 * and above does; if neither fits, whichever side has more room.
 */
export function placePopover(anchor: Rect, size: Size, viewportWidth: number, band: Band): Placement {
  const centre = (anchor.left + anchor.right) / 2;
  const preferred = centre > viewportWidth / 2 ? anchor.right - size.width : anchor.left;
  const left = Math.max(POPOVER_EDGE, Math.min(preferred, viewportWidth - POPOVER_EDGE - size.width));

  const roomBelow = band.bottom - POPOVER_EDGE - (anchor.bottom + POPOVER_GAP);
  const roomAbove = anchor.top - POPOVER_GAP - (band.top + POPOVER_EDGE);
  const side: Placement["side"] =
    size.height <= roomBelow ? "below" : size.height <= roomAbove ? "above" : roomBelow >= roomAbove ? "below" : "above";
  const top = side === "below" ? anchor.bottom + POPOVER_GAP : anchor.top - POPOVER_GAP - size.height;

  const caret =
    size.width <= 2 * CARET_INSET ? size.width / 2 : clamp(centre - left, CARET_INSET, size.width - CARET_INSET);

  return { left, top, side, caret };
}

/**
 * The vertical band between a sticky table head and a sticky table foot.
 * A head or foot that is not laid out (height 0 — the phone's card layout
 * hides the head, jsdom lays out nothing) does not narrow it.
 */
export function visibleBand(viewportHeight: number, head: (Rect & { height: number }) | null, foot: (Rect & { height: number }) | null): Band {
  const top = head !== null && head.height > 0 ? Math.max(0, head.bottom) : 0;
  const bottom = foot !== null && foot.height > 0 ? Math.min(viewportHeight, foot.top) : viewportHeight;
  return { top, bottom };
}

/**
 * Whether the trigger can still be seen: its centre inside the band. Once it
 * scrolls under the sticky head (or off screen), the popover would float over
 * rows it no longer belongs to — it closes instead. An anchor that is not laid
 * out at all (all zeros) cannot be judged and counts as seen.
 */
export function anchorInBand(anchor: Rect, band: Band): boolean {
  if (anchor.top === 0 && anchor.bottom === 0 && anchor.left === 0 && anchor.right === 0) return true;
  const middle = (anchor.top + anchor.bottom) / 2;
  return middle > band.top && middle < band.bottom;
}
